import { supabase } from './supabaseClient';

const LS_KEY = 'tc-device-session-id';
const LS_KICKED = 'tc-session-kicked';

const newId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const deviceLabel = () =>
  typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 200) : null;

export const getLocalDeviceSessionId = (): string | null => {
  try { return localStorage.getItem(LS_KEY); } catch { return null; }
};
const setLocal = (id: string) => { try { localStorage.setItem(LS_KEY, id); } catch {} };
export const clearDeviceSession = () => { try { localStorage.removeItem(LS_KEY); } catch {} };

/** Consomme le flag « éjecté » (pour afficher la bannière une fois). */
export const consumeKickedFlag = (): boolean => {
  try {
    const v = localStorage.getItem(LS_KICKED);
    if (v) localStorage.removeItem(LS_KICKED);
    return v === '1';
  } catch { return false; }
};
const markKicked = () => { try { localStorage.setItem(LS_KICKED, '1'); } catch {} };

/**
 * À la connexion : réclame la session active (supersède les autres appareils).
 * Ne jette JAMAIS — un échec DB (table absente / réseau) ne doit pas casser le login.
 */
export const claimDeviceSession = async (userId: string): Promise<string> => {
  const id = newId();
  setLocal(id);
  try {
    await supabase.schema('api').from('user_sessions').upsert({
      user_id: userId, active_session_id: id, device_label: deviceLabel(),
      updated_at: new Date().toISOString(),
    });
  } catch (e) {
    console.warn('[sessionGuard] claim upsert failed (non-fatal):', e);
  }
  return id;
};

/** Bonus C : révoque les refresh tokens des AUTRES appareils (nettoyage serveur). */
export const revokeOtherSessions = async (): Promise<void> => {
  try { await supabase.auth.signOut({ scope: 'others' }); }
  catch (e) { console.warn('[sessionGuard] signOut others non supporté:', e); }
};

/**
 * Au restore : sommes-nous toujours la session active ? 'superseded' => éjecter.
 * Fail-open : toute erreur → 'ok' (on n'éjecte pas un user légitime sur un blip).
 */
export const verifyDeviceSession = async (userId: string): Promise<'ok' | 'superseded'> => {
  try {
    const local = getLocalDeviceSessionId();
    const { data } = await supabase.schema('api').from('user_sessions')
      .select('active_session_id').eq('user_id', userId).maybeSingle();
    const remote = (data as { active_session_id?: string } | null)?.active_session_id ?? null;
    if (!local || !remote) { await claimDeviceSession(userId); return 'ok'; } // adopte
    return local === remote ? 'ok' : 'superseded';
  } catch (e) {
    console.warn('[sessionGuard] verify failed (fail-open):', e);
    return 'ok';
  }
};

/** Abonnement Realtime : éjection quand la ligne passe à un autre id. */
export const subscribeDeviceSession = (userId: string, onSuperseded: () => void): (() => void) => {
  const channel = supabase
    .channel(`user_session:${userId}`)
    .on('postgres_changes',
      { event: 'UPDATE', schema: 'api', table: 'user_sessions', filter: `user_id=eq.${userId}` },
      (payload: { new?: { active_session_id?: string } }) => {
        const remote = payload?.new?.active_session_id;
        if (remote && remote !== getLocalDeviceSessionId()) onSuperseded();
      })
    .subscribe();
  return () => { supabase.removeChannel(channel); };
};

/** Éjection : marque + déconnecte cet appareil (auth state change videra le user). */
export const enforceKick = async (): Promise<void> => {
  markKicked();
  clearDeviceSession();
  try { await supabase.auth.signOut({ scope: 'local' }); } catch {}
};
